import React, { useEffect, useState } from 'react';
import {
  Badge,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Icon,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import CustomModal from 'components/modal/Modal';
import { Dropdown } from 'components/ui/Dropdown';
import Button from 'components/ui/Button';
import { MdAdd } from 'react-icons/md';
import {
  usePatologicos,
  useUpdateHeredofamiliares,
  useUpdateNoPatologicos,
  useUpdatePatologicos,
} from 'hooks/useAntecedentes';
import {
  AntecedentesHeredofamiliar,
  AntecedentesNoPatologico,
  AntecedentesPatologico,
} from 'types/models';

// ── Types & constants ─────────────────────────────────────────────────────────

type AnteTipo = 'patologico' | 'heredofamiliar' | 'no_patologico';
type AnteItem  = { tipo: AnteTipo; key: string; label: string; isCustom?: boolean };
type AlergiaItem = { descripcion: string; reaccion?: string };

const TIPO_LABELS: Record<AnteTipo, string> = {
  patologico: 'Patológico',
  heredofamiliar: 'Heredo-familiar',
  no_patologico: 'No patológico',
};

const TIPO_COLORS: Record<AnteTipo, string> = {
  patologico: 'red',
  heredofamiliar: 'blue',
  no_patologico: 'green',
};

const CONDICIONES: Record<AnteTipo, { key: string; label: string }[]> = {
  patologico: [
    { key: 'diabetesMellitus',          label: 'Diabetes Mellitus'    },
    { key: 'hipertensionArterial',      label: 'Hipertensión Arterial'},
    { key: 'cardiopatias',              label: 'Cardiopatías'         },
    { key: 'enfermedadesRespiratorias', label: 'Enf. Respiratorias'  },
    { key: 'enfermedadesRenales',       label: 'Enf. Renales'        },
    { key: 'cancer',                    label: 'Cáncer'              },
    { key: 'tuberculosis',              label: 'Tuberculosis'        },
    { key: 'hepatitis',                 label: 'Hepatitis'           },
    { key: 'vihSida',                   label: 'VIH/SIDA'           },
    { key: 'covid19',                   label: 'COVID-19'            },
    { key: 'epilepsia',                 label: 'Epilepsia'           },
    { key: 'accidenteCerebrovascular',  label: 'ACV'                 },
    { key: 'depresion',                 label: 'Depresión'           },
    { key: 'ansiedad',                  label: 'Ansiedad'            },
    { key: 'transfusiones',             label: 'Transfusiones'       },
  ],
  heredofamiliar: [
    { key: 'diabetes',                  label: 'Diabetes'            },
    { key: 'hipertension',              label: 'Hipertensión'        },
    { key: 'cardiopatias',              label: 'Cardiopatías'        },
    { key: 'cancer',                    label: 'Cáncer'              },
    { key: 'enfermedadesRespiratorias', label: 'Enf. Respiratorias' },
    { key: 'enfermedadesRenales',       label: 'Enf. Renales'       },
    { key: 'enfermedadesNeurologicas',  label: 'Enf. Neurológicas'  },
    { key: 'enfermedadesMentales',      label: 'Enf. Mentales'      },
  ],
  no_patologico: [
    { key: 'tabaquismo',      label: 'Tabaquismo'      },
    { key: 'alcoholismo',     label: 'Alcoholismo'     },
    { key: 'drogas',          label: 'Drogas'          },
    { key: 'cafe',            label: 'Café'            },
    { key: 'actividadFisica', label: 'Actividad Física'},
  ],
};

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  initialTab?: number;
}

export default function AddAntecedentesAlergiaModal({
  isOpen, onClose, patientId, initialTab = 0,
}: Props) {
  const toast      = useToast();
  const labelColor = useColorModeValue('gray.700', 'gray.200');
  const hintColor  = useColorModeValue('gray.400', 'gray.500');

  const [tabIndex, setTabIndex] = useState(initialTab);

  // ── Antecedentes state
  const [antecedentes, setAntecedentes] = useState<AnteItem[]>([]);
  const [anteType,     setAnteType]     = useState<AnteTipo>('patologico');
  const [anteCond,     setAnteCond]     = useState('');
  const [anteCustom,   setAnteCustom]   = useState('');

  // ── Alergias state
  const [alergias,     setAlergias]     = useState<AlergiaItem[]>([]);
  const [alergiaDesc,  setAlergiaDesc]  = useState('');
  const [alergiaReacc, setAlergiaReacc] = useState('');

  const { data: patData } = usePatologicos(patientId, isOpen);
  const updateHeredo  = useUpdateHeredofamiliares(patientId);
  const updatePat     = useUpdatePatologicos(patientId);
  const updateNoPat   = useUpdateNoPatologicos(patientId);
  const isPending     = updateHeredo.isPending || updatePat.isPending || updateNoPat.isPending;

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setTabIndex(initialTab);
    setAntecedentes([]); setAlergias([]);
    setAnteCond(''); setAnteCustom('');
    setAlergiaDesc(''); setAlergiaReacc('');
  }, [isOpen, initialTab]);

  // ── Antecedentes logic
  const handleCondSelect = (val: string | number) => {
    const v = String(val);
    if (v === '__custom__') { setAnteCond('__custom__'); return; }
    if (antecedentes.some(a => a.tipo === anteType && a.key === v)) return;
    const found = CONDICIONES[anteType].find(c => c.key === v);
    if (!found) return;
    setAntecedentes(prev => [...prev, { tipo: anteType, key: v, label: found.label }]);
    // leave anteCond blank so dropdown resets to placeholder
  };

  const addCustomAnte = () => {
    if (!anteCustom.trim()) return;
    setAntecedentes(prev => [...prev, { tipo: anteType, key: '__custom__', label: anteCustom.trim(), isCustom: true }]);
    setAnteCond(''); setAnteCustom('');
  };

  // ── Alergias logic
  const addAlergia = () => {
    if (!alergiaDesc.trim()) return;
    setAlergias(prev => [...prev, { descripcion: alergiaDesc.trim(), reaccion: alergiaReacc.trim() || undefined }]);
    setAlergiaDesc(''); setAlergiaReacc('');
  };

  // ── Save
  const handleSave = async () => {
    if (antecedentes.length === 0 && alergias.length === 0) { onClose(); return; }
    try {
      const patches: Promise<any>[] = [];

      const heredoItems = antecedentes.filter(a => a.tipo === 'heredofamiliar');
      const patItems    = antecedentes.filter(a => a.tipo === 'patologico');
      const noPatItems  = antecedentes.filter(a => a.tipo === 'no_patologico');

      if (heredoItems.length > 0) {
        const p: Record<string, any> = {};
        heredoItems.filter(i => !i.isCustom).forEach(i => { p[i.key] = true; });
        const custom = heredoItems.filter(i => i.isCustom);
        if (custom.length) p.otros = custom.map(i => ({ enfermedad: i.label }));
        patches.push(updateHeredo.mutateAsync(p as Partial<AntecedentesHeredofamiliar>));
      }

      if (patItems.length > 0 || alergias.length > 0) {
        const p: Record<string, any> = {};
        patItems.filter(i => !i.isCustom).forEach(i => { p[i.key] = true; });
        const patCustom = patItems.filter(i => i.isCustom);
        if (patCustom.length) p.otros = patCustom.map(i => ({ descripcion: i.label }));
        if (alergias.length > 0) {
          const existing = ((patData?.alergiasMedicamentos ?? []) as AlergiaItem[]);
          p.alergiasMedicamentos = [...existing, ...alergias.map(a => ({ descripcion: a.descripcion, reaccion: a.reaccion }))];
        }
        patches.push(updatePat.mutateAsync(p as Partial<AntecedentesPatologico>));
      }

      if (noPatItems.length > 0) {
        const p: Record<string, any> = {};
        noPatItems.filter(i => !i.isCustom).forEach(i => { p[i.key] = true; });
        const custom = noPatItems.filter(i => i.isCustom);
        if (custom.length) p.otrosHabitos = custom.map(i => i.label).join(', ');
        patches.push(updateNoPat.mutateAsync(p as Partial<AntecedentesNoPatologico>));
      }

      await Promise.all(patches);
      toast({ title: 'Guardado', status: 'success', duration: 2000, position: 'top-right' });
      onClose();
    } catch {
      toast({ title: 'Error al guardar', status: 'error', duration: 3000, position: 'top-right' });
    }
  };

  const Opt = () => <Text as="span" fontSize="xs" color="gray.400" ms="4px">Opcional</Text>;

  const availableConditions = CONDICIONES[anteType]
    .filter(c => !antecedentes.some(a => a.tipo === anteType && a.key === c.key))
    .map(c => ({ label: c.label, value: c.key }));

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Agregar Antecedentes y Alergias"
      size="lg"
      onPrimaryClick={handleSave}
      primaryBtnLabel="Guardar"
      primaryBtnLoading={isPending}
      primaryBtnDisabled={antecedentes.length === 0 && alergias.length === 0}
      scrollBehavior="inside"
    >
      <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="brand" variant="line">
        <TabList mb="20px">
          <Tab fontSize="sm" fontWeight="600">
            Antecedentes
            {antecedentes.length > 0 && (
              <Badge ml="6px" colorScheme="brand" borderRadius="full" fontSize="10px" px="6px">
                {antecedentes.length}
              </Badge>
            )}
          </Tab>
          <Tab fontSize="sm" fontWeight="600">
            Alergias
            {alergias.length > 0 && (
              <Badge ml="6px" colorScheme="orange" borderRadius="full" fontSize="10px" px="6px">
                {alergias.length}
              </Badge>
            )}
          </Tab>
        </TabList>

        <TabPanels>
          {/* ── Tab Antecedentes ── */}
          <TabPanel px="0" py="0">
            <Flex direction="column" gap="16px">
              {/* Selector */}
              <Grid templateColumns="1fr 1fr" gap="10px">
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="600" color={labelColor} mb="6px">Tipo</FormLabel>
                  <Dropdown
                    items={[
                      { label: 'Patológico',      value: 'patologico'     },
                      { label: 'Heredo-familiar', value: 'heredofamiliar' },
                      { label: 'No patológico',   value: 'no_patologico'  },
                    ]}
                    label={TIPO_LABELS[anteType]}
                    onSelect={(val) => { setAnteType(String(val) as AnteTipo); setAnteCond(''); setAnteCustom(''); }}
                    size="sm" w="full" variant="outline" showIcon borderRadius="7px"
                    fontSize="sm" fontWeight="400" menuWidth="200px" itemPx="12px" itemPy="8px"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="600" color={labelColor} mb="6px">Condición</FormLabel>
                  {anteCond === '__custom__' ? (
                    <Input
                      size="sm" borderRadius="7px" placeholder="Escribir condición..."
                      value={anteCustom} onChange={e => setAnteCustom(e.target.value)} autoFocus
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); addCustomAnte(); }
                        if (e.key === 'Escape') { setAnteCond(''); setAnteCustom(''); }
                      }}
                    />
                  ) : (
                    <Dropdown
                      items={[
                        ...availableConditions,
                        { label: 'Otra (manual)...', value: '__custom__' },
                      ]}
                      label="Seleccionar condición..."
                      onSelect={handleCondSelect}
                      size="sm" w="full" variant="outline" showIcon borderRadius="7px"
                      fontSize="sm" fontWeight="400" menuWidth="220px" listMaxH="200px"
                      itemPx="12px" itemPy="8px"
                    />
                  )}
                </FormControl>
              </Grid>

              <Text fontSize="xs" color={hintColor}>
                {anteCond === '__custom__'
                  ? 'Enter para agregar · Esc para cancelar'
                  : 'Selecciona una condición para agregarla automáticamente.'}
              </Text>

              {/* Tags agregados */}
              {antecedentes.length > 0 && (
                <Flex flexWrap="wrap" gap="6px">
                  {antecedentes.map((a, idx) => (
                    <Tag key={idx} size="sm" colorScheme={TIPO_COLORS[a.tipo]} borderRadius="full">
                      <TagLabel>{a.label} · {TIPO_LABELS[a.tipo]}</TagLabel>
                      <TagCloseButton onClick={() => setAntecedentes(prev => prev.filter((_, i) => i !== idx))} />
                    </Tag>
                  ))}
                </Flex>
              )}
            </Flex>
          </TabPanel>

          {/* ── Tab Alergias ── */}
          <TabPanel px="0" py="0">
            <Flex direction="column" gap="16px">
              {/* Inputs siempre visibles */}
              <Grid templateColumns="1fr 1fr" gap="10px">
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="600" color={labelColor} mb="6px">Alergia</FormLabel>
                  <Input
                    size="sm" borderRadius="7px"
                    placeholder="Medicamento, alimento, látex..."
                    value={alergiaDesc} onChange={e => setAlergiaDesc(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAlergia(); } }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="600" color={labelColor} mb="6px">
                    Reacción <Opt />
                  </FormLabel>
                  <Input
                    size="sm" borderRadius="7px" placeholder="Anafilaxia, urticaria..."
                    value={alergiaReacc} onChange={e => setAlergiaReacc(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAlergia(); } }}
                  />
                </FormControl>
              </Grid>

              <Button
                size="sm" variant="outline" colorScheme="orange"
                leftIcon={<Icon as={MdAdd} />}
                onClick={addAlergia}
                isDisabled={!alergiaDesc.trim()}
                alignSelf="flex-start"
              >
                Agregar alergia
              </Button>

              {/* Tags agregados */}
              {alergias.length > 0 && (
                <Flex flexWrap="wrap" gap="6px">
                  {alergias.map((a, idx) => (
                    <Tag key={idx} size="sm" colorScheme="orange" borderRadius="full">
                      <TagLabel>{a.descripcion}{a.reaccion ? ` — ${a.reaccion}` : ''}</TagLabel>
                      <TagCloseButton onClick={() => setAlergias(prev => prev.filter((_, i) => i !== idx))} />
                    </Tag>
                  ))}
                </Flex>
              )}
            </Flex>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </CustomModal>
  );
}
